import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Divider,
  TextField,
  Slider,
  Button,
  Popper,
  Grow,
  ClickAwayListener,
  Switch
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';

export interface KioskAdditionalSettings {
  productDetailMode?: 'simple' | 'detailed'; // simple: Hızlı sepet ekleme, detailed: Modal ile detaylı seçim
  addToCartButton?: {
    text?: string;
    backgroundColor?: string;
    textColor?: string;
    borderRadius?: number;
    fontSize?: number;
    height?: number;
  };
  // Sipariş Ver butonuna basıldığında çıkan tip seçenekleri (Paket / Burada Yiyeceğim).
  // İkisi de varsayılan açık. Yönetici yalnızca BİRİNİ kapatabilir; tek tip açıkken
  // kiosk seçim modalını atlar ve doğrudan o tip ile sipariş ekranına gider.
  orderTypeButtons?: {
    takeawayEnabled?: boolean; // Paket Sipariş
    dineInEnabled?: boolean;   // Burada Yiyeceğim
  };
}

interface KioskAdditionalSettingsTabProps {
  settings: KioskAdditionalSettings;
  onSettingsChange: (settings: KioskAdditionalSettings) => void;
}

const KioskAdditionalSettingsTab: React.FC<KioskAdditionalSettingsTabProps> = ({
  settings,
  onSettingsChange
}) => {
  const { t } = useTranslation('salesSystem');
  
  // Renk seçici state'leri
  const [bgColorPickerOpen, setBgColorPickerOpen] = useState(false);
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  const [bgColorAnchorEl, setBgColorAnchorEl] = useState<HTMLElement | null>(null);
  const [textColorAnchorEl, setTextColorAnchorEl] = useState<HTMLElement | null>(null);

  // Sipariş tipi butonları (varsayılan ikisi de açık)
  const takeawayEnabled = settings.orderTypeButtons?.takeawayEnabled !== false;
  const dineInEnabled = settings.orderTypeButtons?.dineInEnabled !== false;
  // Kullanıcı son açık olan tipi de kapatmaya çalışırsa uyarı göster
  const [orderTypeWarning, setOrderTypeWarning] = useState(false);

  const handleOrderTypeToggle = (key: 'takeawayEnabled' | 'dineInEnabled', value: boolean) => {
    const next = {
      takeawayEnabled,
      dineInEnabled,
      [key]: value
    };
    // En az bir sipariş tipi açık kalmalı — ikisi birden kapatılamaz
    if (!next.takeawayEnabled && !next.dineInEnabled) {
      setOrderTypeWarning(true);
      return;
    }
    setOrderTypeWarning(false);
    onSettingsChange({
      ...settings,
      orderTypeButtons: next
    });
  };
  
  // Varsayılan buton ayarları
  const defaultButtonSettings = {
    text: t('kioskAdditionalTab.defaults.addToCartText'),
    backgroundColor: '#2196F3',
    textColor: '#FFFFFF',
    borderRadius: 12,
    fontSize: 18,
    height: 50
  };
  
  const buttonSettings = settings.addToCartButton || defaultButtonSettings;
  
  const handleProductDetailModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as 'simple' | 'detailed';
    onSettingsChange({
      ...settings,
      productDetailMode: newMode
    });
  };
  
  const handleButtonSettingChange = (key: string, value: any) => {
    onSettingsChange({
      ...settings,
      addToCartButton: {
        ...buttonSettings,
        [key]: value
      }
    });
  };
  
  return (
    <Paper elevation={0} sx={{ p: 2, minHeight: '500px' }}>
      <Typography variant="h6" gutterBottom>{t('kioskAdditionalTab.title')}</Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        {t('kioskAdditionalTab.subtitle')}
      </Typography>

      {/* Sipariş Tipi Butonları (Paket / Burada Yiyeceğim) */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StorefrontOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight="bold">
            {t('kioskAdditionalTab.orderTypeButtons.title')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('kioskAdditionalTab.orderTypeButtons.description')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Paket Sipariş */}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingBagOutlinedIcon sx={{ mr: 1.5, color: takeawayEnabled ? 'warning.main' : 'text.disabled' }} />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t('kioskAdditionalTab.orderTypeButtons.takeaway')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('kioskAdditionalTab.orderTypeButtons.takeawayDesc')}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={takeawayEnabled}
              onChange={(e) => handleOrderTypeToggle('takeawayEnabled', e.target.checked)}
              color="warning"
            />
          </Paper>

          {/* Burada Yiyeceğim */}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RestaurantOutlinedIcon sx={{ mr: 1.5, color: dineInEnabled ? 'success.main' : 'text.disabled' }} />
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {t('kioskAdditionalTab.orderTypeButtons.dineIn')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('kioskAdditionalTab.orderTypeButtons.dineInDesc')}
                </Typography>
              </Box>
            </Box>
            <Switch
              checked={dineInEnabled}
              onChange={(e) => handleOrderTypeToggle('dineInEnabled', e.target.checked)}
              color="success"
            />
          </Paper>
        </Box>

        {orderTypeWarning && (
          <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setOrderTypeWarning(false)}>
            {t('kioskAdditionalTab.orderTypeButtons.bothDisabledWarning')}
          </Alert>
        )}

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          {t('kioskAdditionalTab.orderTypeButtons.note')}
        </Alert>
      </Paper>

      {/* Ürün Ayrıntıları Modu Ayarı */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <InfoOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight="bold">
            {t('kioskAdditionalTab.orderMode.title')}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('kioskAdditionalTab.orderMode.description')}
        </Typography>
        
        <FormControl component="fieldset" fullWidth>
          <RadioGroup
            value={settings.productDetailMode || 'simple'}
            onChange={handleProductDetailModeChange}
          >
            {/* Basit Mod (Varsayılan) */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                cursor: 'pointer',
                border: 2,
                borderColor: (settings.productDetailMode || 'simple') === 'simple' ? 'primary.main' : 'divider',
                bgcolor: (settings.productDetailMode || 'simple') === 'simple' ? 'primary.50' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50'
                }
              }}
              onClick={() => onSettingsChange({ ...settings, productDetailMode: 'simple' })}
            >
              <FormControlLabel
                value="simple"
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <ShoppingCartIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                      <Typography variant="body1" fontWeight="bold">
                        {t('kioskAdditionalTab.orderMode.quick.title')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                      {t('kioskAdditionalTab.orderMode.quick.description')}
                      <br />
                      <strong>{t('kioskAdditionalTab.common.usage')}</strong> {t('kioskAdditionalTab.orderMode.quick.usage')}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>
            
            {/* Detaylı Mod */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                cursor: 'pointer',
                border: 2,
                borderColor: settings.productDetailMode === 'detailed' ? 'secondary.main' : 'divider',
                bgcolor: settings.productDetailMode === 'detailed' ? 'secondary.50' : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'secondary.main',
                  bgcolor: 'secondary.50'
                }
              }}
              onClick={() => onSettingsChange({ ...settings, productDetailMode: 'detailed' })}
            >
              <FormControlLabel
                value="detailed"
                control={<Radio />}
                label={
                  <Box sx={{ ml: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <ListAltIcon sx={{ mr: 1, fontSize: 20, color: 'secondary.main' }} />
                      <Typography variant="body1" fontWeight="bold">
                        {t('kioskAdditionalTab.orderMode.detailed.title')}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                      {t('kioskAdditionalTab.orderMode.detailed.description')}
                      <br />
                      <strong>{t('kioskAdditionalTab.common.usage')}</strong> {t('kioskAdditionalTab.orderMode.detailed.usage')}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>
          </RadioGroup>
        </FormControl>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Bilgilendirme */}
        <Alert severity="info" icon={<InfoOutlinedIcon />}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            {t('kioskAdditionalTab.preview.title')}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            {(settings.productDetailMode || 'simple') === 'simple' ? (
              <>
                <strong>{t('kioskAdditionalTab.orderMode.quick.title')}:</strong> {t('kioskAdditionalTab.preview.quick')}
              </>
            ) : (
              <>
                <strong>{t('kioskAdditionalTab.orderMode.detailed.title')}:</strong> {t('kioskAdditionalTab.preview.detailed')}
              </>
            )}
          </Typography>
        </Alert>
      </Paper>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Sepete Ekle Butonu Özelleştirme */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ShoppingCartIcon sx={{ mr: 1, color: 'success.main' }} />
          <Typography variant="subtitle1" fontWeight="bold">
            {t('kioskAdditionalTab.buttonCustomization.title')}
          </Typography>
        </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('kioskAdditionalTab.buttonCustomization.description')}
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Buton Metni */}
          <TextField
            fullWidth
            size="small"
            label={t('kioskAdditionalTab.fields.buttonText')}
            value={buttonSettings.text}
            onChange={(e) => handleButtonSettingChange('text', e.target.value)}
          />
          
          {/* Arka Plan Rengi */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('kioskAdditionalTab.fields.backgroundColor')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Box
                onClick={(e) => {
                  setBgColorAnchorEl(e.currentTarget);
                  setBgColorPickerOpen(!bgColorPickerOpen);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: buttonSettings.backgroundColor,
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 1
                  }
                }}
              />
              <TextField
                size="small"
                value={buttonSettings.backgroundColor}
                onChange={(e) => handleButtonSettingChange('backgroundColor', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* Arka Plan Renk Seçici */}
            <Popper 
              open={bgColorPickerOpen} 
              anchorEl={bgColorAnchorEl}
              placement="bottom-start"
              transition
              style={{ zIndex: 1400 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
                    <ClickAwayListener onClickAway={() => setBgColorPickerOpen(false)}>
                      <Box>
                        <HexColorPicker
                          color={buttonSettings.backgroundColor}
                          onChange={(color) => handleButtonSettingChange('backgroundColor', color)}
                        />
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            size="small"
                            onClick={async () => {
                              try {
                                // @ts-ignore
                                if (window.EyeDropper) {
                                  // @ts-ignore
                                  const eyeDropper = new window.EyeDropper();
                                  const result = await eyeDropper.open();
                                  handleButtonSettingChange('backgroundColor', result.sRGBHex);
                                } else {
                                  alert(t('kioskAdditionalTab.alerts.eyedropperNotSupported'));
                                }
                              } catch (err) {
                                console.log('Renk örnekleme iptal edildi');
                              }
                            }}
                            variant="outlined"
                            startIcon={<ColorLensIcon />}
                          >
                            {t('kioskAdditionalTab.actions.pickColor')}
                          </Button>
                          <Button
                            fullWidth
                            size="small"
                            onClick={() => setBgColorPickerOpen(false)}
                            variant="outlined"
                          >
                            {t('kioskAdditionalTab.actions.close')}
                          </Button>
                        </Box>
                      </Box>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
          
          {/* Yazı Rengi */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('kioskAdditionalTab.fields.textColor')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Box
                onClick={(e) => {
                  setTextColorAnchorEl(e.currentTarget);
                  setTextColorPickerOpen(!textColorPickerOpen);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: buttonSettings.textColor,
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 1
                  }
                }}
              />
              <TextField
                size="small"
                value={buttonSettings.textColor}
                onChange={(e) => handleButtonSettingChange('textColor', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Box>
            
            {/* Yazı Renk Seçici */}
            <Popper 
              open={textColorPickerOpen} 
              anchorEl={textColorAnchorEl}
              placement="bottom-start"
              transition
              style={{ zIndex: 1400 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps}>
                  <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
                    <ClickAwayListener onClickAway={() => setTextColorPickerOpen(false)}>
                      <Box>
                        <HexColorPicker
                          color={buttonSettings.textColor}
                          onChange={(color) => handleButtonSettingChange('textColor', color)}
                        />
                        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                          <Button
                            fullWidth
                            size="small"
                            onClick={async () => {
                              try {
                                // @ts-ignore
                                if (window.EyeDropper) {
                                  // @ts-ignore
                                  const eyeDropper = new window.EyeDropper();
                                  const result = await eyeDropper.open();
                                  handleButtonSettingChange('textColor', result.sRGBHex);
                                } else {
                                  alert(t('kioskAdditionalTab.alerts.eyedropperNotSupported'));
                                }
                              } catch (err) {
                                console.log('Renk örnekleme iptal edildi');
                              }
                            }}
                            variant="outlined"
                            startIcon={<ColorLensIcon />}
                          >
                            {t('kioskAdditionalTab.actions.pickColor')}
                          </Button>
                          <Button
                            fullWidth
                            size="small"
                            onClick={() => setTextColorPickerOpen(false)}
                            variant="outlined"
                          >
                            {t('kioskAdditionalTab.actions.close')}
                          </Button>
                        </Box>
                      </Box>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
          
          {/* Yazı Boyutu */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('kioskAdditionalTab.fields.fontSize', { value: buttonSettings.fontSize })}
            </Typography>
            <Slider
              value={buttonSettings.fontSize}
              onChange={(e, value) => handleButtonSettingChange('fontSize', value as number)}
              min={14}
              max={24}
              step={2}
              marks
              valueLabelDisplay="auto"
            />
          </Box>
          
          {/* Buton Yüksekliği */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('kioskAdditionalTab.fields.buttonHeight', { value: buttonSettings.height })}
            </Typography>
            <Slider
              value={buttonSettings.height}
              onChange={(e, value) => handleButtonSettingChange('height', value as number)}
              min={40}
              max={70}
              step={5}
              marks
              valueLabelDisplay="auto"
            />
          </Box>
          
          {/* Köşe Yuvarlaklığı */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('kioskAdditionalTab.fields.borderRadius', { value: buttonSettings.borderRadius })}
            </Typography>
            <Slider
              value={buttonSettings.borderRadius}
              onChange={(e, value) => handleButtonSettingChange('borderRadius', value as number)}
              min={0}
              max={20}
              step={2}
              marks
              valueLabelDisplay="auto"
            />
          </Box>
          
          {/* Buton Önizlemesi */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
              {t('kioskAdditionalTab.preview.label')}
            </Typography>
            <Button
              fullWidth
              variant="contained"
              startIcon={<ShoppingCartIcon />}
              sx={{
                bgcolor: buttonSettings.backgroundColor,
                color: buttonSettings.textColor,
                fontSize: `${buttonSettings.fontSize}px`,
                height: `${buttonSettings.height}px`,
                borderRadius: `${buttonSettings.borderRadius}px`,
                '&:hover': {
                  bgcolor: buttonSettings.backgroundColor,
                  opacity: 0.9
                }
              }}
            >
              {buttonSettings.text}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Paper>
  );
};

export default KioskAdditionalSettingsTab;

